// Contrat transverse de l'API (enveloppe + erreurs) + premiers types métier.
// Portés du backend (App\Http\Concerns\ApiResponse + Resources). Voir
// docs/frontend-web/05-reference-api.md et 06-modele-de-domaine.md.

export interface ApiSuccess<T> {
  status: 'OK';
  data: T;
  message: string | null;
}

export interface ApiFailure {
  status: 'KO';
  code: string;
  message: string | null;
  errors: Record<string, string[]>;
}

export type ApiEnvelope<T> = ApiSuccess<T> | ApiFailure;

// Erreur typée levée par le client API sur réponse KO (ou HTTP non-2xx).
export class ApiError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly errors: Record<string, string[]>;

  constructor(
    code: string,
    message: string | null,
    httpStatus: number,
    errors: Record<string, string[]> = {},
  ) {
    super(message ?? code);
    this.name = 'ApiError';
    this.code = code;
    this.httpStatus = httpStatus;
    this.errors = errors;
  }
}

// --- Utilisateur courant (GET /api/me) ---

export type ThemePref = 'auto' | 'light' | 'dark';
export type UserStatus = 'active' | 'blocked' | 'frozen' | 'deleted';

export interface User {
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  email_verified: boolean;
  phone: string | null;
  locale: string;
  theme: ThemePref;
  status: UserStatus;
  searchable: boolean;
  has_password: boolean;
  avatar_url: string | null;
  avatar_import_pending: boolean;
  profile_public: boolean;
  created_at: string;
}

// --- Souvenirs (GET /api/memories) ---

export type Visibility = 'private' | 'shared' | 'public';
export interface MemoryOwner {
  uuid: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

export interface Memory {
  uuid: string;
  owner: MemoryOwner;
  title: string;
  description: string | null;
  visibility: Visibility;
  start_date: string | null;
  end_date: string | null;
  cover_url: string | null;
  location: {
    name: string | null;
    address: string | null;
    latitude: number | null;
    longitude: number | null;
  };
  is_favorite: boolean;
  is_hidden: boolean;
  notifications_enabled: boolean;
  moments_count: number;
  total_likes: number;
  total_comments: number;
  // Moments non lus PAR LE VIEWER (décrit sa relation au souvenir, pas le souvenir).
  unread_count: number;
  // Personnes distinctes ayant accès au souvenir, propriétaire exclu. Vaut 0 sur un
  // souvenir privé — ne l'afficher que là où il a un sens.
  participants_count: number;
  /**
   * Disposition des moments VISIBLES par le viewer : positions, tailles et TYPE de chacun.
   * Servie pour dessiner un squelette de grille conforme, elle permet aussi de dériver le
   * mix de médias sans requête supplémentaire (cf. lib/library/mediaMix).
   * Ni limitée ni tronquée, et de même population que `moments_count`.
   */
  grid_layout?: Array<{
    row: number;
    col: number;
    w: number;
    h: number;
    type: string;
    // Case volontairement vide (§8.5) : elle occupe sa place sans contenu, et porte
    // `type: 'text'` — sans ce drapeau, indiscernable d'un vrai moment texte.
    is_empty?: boolean;
  }>;
  /**
   * Rôle du lecteur courant. Contraint aux trois valeurs vivantes, comme côté mobile — mais le
   * parse reste tolérant : c'est un enum FERMÉ qui avait vidé les écrans au lot 5A, une valeur
   * inconnue faisant échouer la réponse entière. Ici le typage n'engage que l'affichage ; le
   * serveur reste seul juge des droits, et `roleLabel` (lib/roleLabel) couvre la valeur inattendue.
   */
  viewer_role: ViewerRole;
  /**
   * Version de la grille, servie par l'API depuis le « lot verrou ». Captée à l'ouverture de
   * l'éditeur et renvoyée à l'enregistrement : le serveur refuse alors (409 MEMORY_EDIT_CONFLICT)
   * si un tiers a sauvegardé entre-temps.
   *
   * Le web ne la lisait pas — son éditeur écrasait donc en silence le travail d'un autre (lot 15).
   */
  content_version: number;
  can_leave: boolean;
  hidden_by_admin?: boolean;
  created_at: string;
  updated_at: string;
}

export interface MemoriesPage {
  memories: Memory[];
  next_cursor: string | null;
  prev_cursor: string | null;
  /**
   * Mode de pagination de CETTE réponse — source de vérité (cf. docs/backend/api.md).
   * Certains tris portent sur un agrégat ou un JOIN et basculent le serveur en `offset`,
   * où `next_cursor` reste null en permanence. Optionnel : un backend antérieur ne le
   * renvoie pas, l'absence vaut alors `cursor`.
   */
  pagination?: { mode: 'cursor' | 'offset' };
}

export interface GeoSuggestion {
  label: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

export interface MapMemory {
  uuid: string;
  title: string;
  latitude: number;
  longitude: number;
  location_name: string | null;
  start_date: string | null;
  end_date: string | null;
  cover_url: string | null;
  owner: {
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

// --- Moments (GET /api/memories/{uuid}/moments) ---

export interface RichRun {
  text: string;
  font?: 'body' | 'display' | 'serif' | 'mono' | 'hand' | 'script';
  size?: 'sm' | 'base' | 'lg' | 'xl';
  color?: string; // rich-text-1..8
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
}

export interface RichBlock {
  align?: 'left' | 'center' | 'right';
  valign?: 'top' | 'center' | 'bottom'; // alignement vertical dans la tuile
  background?: string | null; // rich-bg-1..8
  runs: RichRun[];
}

export interface RichContent {
  version: number;
  blocks: RichBlock[];
}

export interface MomentAuthor {
  uuid: string | null;
  first_name: string | null;
  last_name: string | null;
  deleted?: boolean;
}

export type MomentType = 'text' | 'image' | 'video';
export type MediaStatus = 'pending' | 'processing' | 'ready' | 'failed';

export interface Media {
  uuid: string;
  type: 'image' | 'video';
  usage: 'cover' | 'moment' | 'avatar';
  processing_status: MediaStatus;
  thumbnail_url?: string | null;
  width?: number | null;
  height?: number | null;
}

export interface Moment {
  uuid: string;
  memory_uuid: string;
  type: MomentType;
  is_empty: boolean;
  text_content: string | null;
  rich_content: RichContent | null;
  media_uuid: string | null;
  media_url: string | null; // thumbnail
  media_url_full: string | null; // optimized
  media_status: MediaStatus | null;
  can_download: boolean;
  author: MomentAuthor;
  width: number;
  height: number;
  row_index: number;
  column_index: number;
  sort_order: number;
  is_private: boolean;
  is_locked: boolean;
  masked: false;
  like_count: number;
  comment_count: number;
  is_liked: boolean;
  can_report: boolean;
  created_at: string;
  updated_at: string;
}

// Moment privé masqué (viewer non-owner) : placeholder positionné, sans contenu.
export interface MaskedMoment {
  row_index: number;
  column_index: number;
  width: number;
  height: number;
  is_private: true;
  masked: true;
}

export type MomentOrMasked = Moment | MaskedMoment;

export interface MomentsPage {
  moments: MomentOrMasked[];
  next_cursor: string | null;
  prev_cursor: string | null;
}

// --- Commentaires (§10) ---

export interface CommentAuthor {
  deleted: boolean;
  uuid: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

export interface Comment {
  uuid: string;
  body: string;
  author: CommentAuthor;
  like_count: number;
  liked_by_me: boolean;
  edited: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_report: boolean;
  created_at: string;
  updated_at: string;
}

export interface CommentsPage {
  comments: Comment[];
  next_cursor: string | null;
}

// --- Réseau : amis & groupes ---

export type FriendStatus = 'accepted' | 'pending';
export type FriendDirection = 'sent' | 'received' | null;
export type FriendListStatus = 'accepted' | 'pending_sent' | 'pending_received';

export interface FriendCounterpart {
  uuid: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

export interface Friendship {
  uuid: string;
  status: FriendStatus;
  direction: FriendDirection;
  counterpart: FriendCounterpart | null;
  accepted_at: string | null;
  created_at: string;
}

export interface FriendshipsPage {
  friendships: Friendship[];
  next_cursor: string | null;
  prev_cursor: string | null;
}

export interface UserSearchResult {
  uuid: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  email?: string;
}

export interface UsersSearchPage {
  results: UserSearchResult[];
  next_cursor: string | null;
}

export interface Group {
  uuid: string;
  name: string;
  members_count: number;
  shared_memories_count: number;
  avatar_url: string | null;
  created_at: string;
}

// --- Découverte : profils publics ---

export type RelationshipStatus =
  | 'none'
  | 'friend'
  | 'request_sent'
  | 'request_received'
  | string;

export interface PublicProfile {
  uuid: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  relationship_status: RelationshipStatus;
  friendship_uuid: string | null;
}

export interface PublicProfilesPage {
  results: PublicProfile[];
  next_cursor: string | null;
  prev_cursor: string | null;
}

export interface PublicProfileDetail {
  profile: PublicProfile;
  memories: Memory[];
  next_cursor: string | null;
  prev_cursor: string | null;
}

// --- Partage & accès (T7) ---

/** Rôles PROPOSABLES — miroir de `PermissionRole::assignable()` (lot 3a). */
export const ASSIGNABLE_ROLES = ['reader', 'contributor'] as const;

/** Rôles OBSERVABLES sur un souvenir : les deux attribuables, plus le propriétaire implicite. */
export const VIEWER_ROLES = ['owner', ...ASSIGNABLE_ROLES] as const;

export type ViewerRole = (typeof VIEWER_ROLES)[number];

export type PermissionRole = (typeof ASSIGNABLE_ROLES)[number];

export interface Permission {
  uuid: string;
  subject_type: 'user' | 'group';
  role: PermissionRole;
  target: {
    uuid: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  } | null;
  group: { uuid: string; name: string } | null;
}

export type InvitationStatus =
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'revoked'
  | 'expired';

export interface Invitation {
  uuid: string;
  email: string;
  role: PermissionRole;
  status: InvitationStatus;
  expires_at: string | null;
  created_at: string;
  inviter: {
    uuid: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
  // Chargé côté acceptation (POST /invitations/accept) pour la redirection vers le souvenir.
  memory?: { uuid: string; title: string } | null;
}

// `reader_download` a disparu (lot 3b) : le téléchargement étant universel, il accordait les
// mêmes droits que `reader`. `contributor` le remplace — un lien peut accorder la contribution.
//
// Liste dérivée, jamais recopiée : le <select> de création de lien citait ses deux valeurs en dur,
// hors de toute source unique. Elles étaient justes — c'est la structure qui ne l'était pas.
export const SHARE_LINK_ROLES = ['reader', 'contributor'] as const;

export type ShareLinkRole = (typeof SHARE_LINK_ROLES)[number];
export type ShareLinkDuration = '1h' | '6h' | '24h' | '7d' | 'permanent';

export interface ShareLink {
  uuid: string;
  url?: string;
  code: string;
  role: ShareLinkRole;
  expires_at: string | null;
  is_permanent: boolean;
  max_uses: number | null;
  uses_count?: number;
  is_active?: boolean;
}

// Résultat d'un claim de lien/code de partage (POST /share/{token}/claim ou /share/code/{code}/claim).
// Le backend octroie une permission directe persistante puis renvoie le souvenir rejoint.
export interface ShareClaimResult {
  memory: {
    uuid: string;
    title: string;
    cover_media_uuid: string | null;
    cover_url: string | null;
  };
  role_granted: ShareLinkRole;
  owner_display_name: string | null;
  claimed?: boolean;
}

// Résultat d'une acceptation d'invitation (POST /invitations/accept).
export interface InvitationAcceptResult {
  invitation: Invitation;
  already_accepted: boolean;
}

// Notification du feed in-app (§14.7, §21.15). Le backend renvoie des CLÉS i18n
// + params (rendu délégué au client, cf. §14.8) — voir lib/notificationRender.
export interface NotificationItem {
  uuid: string;
  type: string;
  title_key: string;
  body_key: string;
  title_params: Record<string, unknown>;
  body_params: Record<string, unknown>;
  data: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
  // Le fil renvoie des GROUPES : cent réactions du même souvenir le même jour forment un élément
  // dont `group_size` vaut 100 et `uuid` désigne la ligne la plus récente — c'est elle qu'on passe
  // à « marquer lu », et le serveur lit alors tout le groupe.
  group_id: string | null;
  group_size: number;
}

export interface NotificationsPage {
  notifications: NotificationItem[];
  next_cursor: string | null;
  prev_cursor: string | null;
}

export interface GroupMembersPage {
  members: UserSearchResult[];
  next_cursor: string | null;
}

// --- Compte : abonnement, quotas, sessions ---

export type SubscriptionState =
  | 'active'
  | 'grace_period'
  | 'frozen'
  | 'canceled'
  | 'unpaid';

export interface Subscription {
  uuid: string;
  plan: Plan;
  state: SubscriptionState;
  started_at: string | null;
  current_period_starts_at: string | null;
  current_period_ends_at: string | null;
  grace_period_ends_at: string | null;
  cancel_at_period_end: boolean;
}

export interface Quotas {
  plan_code: string;
  state: string;
  memories: { used: number; limit: number | null };
  storage: { used_bytes: number; limit_bytes: number | null };
  roles_allowed: string[];
}

export interface Session {
  uuid: string;
  name: string;
  abilities: string[];
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
  is_current: boolean;
}

// --- Abonnements (GET /api/subscriptions/plans, public) ---

export type PlanCode = 'discovery' | 'family' | 'family_plus' | 'family_max';

export interface Plan {
  code: PlanCode;
  /**
   * Le nom affiché, par langue (`{ fr, en }`). ⭐ LES DEUX LANGUES ENSEMBLE : on change de langue
   * sans recharger les données, un nom déjà traduit par le serveur resterait figé. Optionnel — un
   * serveur antérieur au 2026-09-13 ne l'envoie pas, `planName()` retombe alors sur le catalogue.
   */
  name?: Record<string, string>;
  sort_order: number;
  pricing: {
    monthly_cents: number;
    yearly_cents: number;
    currency: string;
  };
  limits: {
    // null = illimité
    max_memories: number | null;
    max_moments_per_memory: number | null;
    max_invitees_per_memory: number | null;
    max_storage_bytes: number | null;
    /**
     * Largeur maximale au téléchargement. ⚠️ CONVENTION INVERSE des autres limites : ici `0` veut
     * dire « qualité maximale, sans compression », jamais « rien ». ⛔ Ce nombre ne s'affiche
     * JAMAIS — le texte se choisit par la valeur, cf. `downloadQualityLabel()`.
     * Optionnel : un serveur antérieur au 2026-09-13 ne l'envoie pas.
     */
    max_download_width?: number;
  };
  allowed_roles: string[];
}
